import _startCase from "lodash-es/startCase";

import { OrgRole } from "services/orbac/types";
import { Role } from "services/rbac/types";

type Group = {
  toAssign: string[];
  toDeny: string[];
  objects: any[];
};

type Privileges = {
  organization: Group[];
  login: Group[];
  "service account": Group[];
  "network policy": Group[];
  role: Group[];
  engine: Group[];
  database: Group[];
  account: Group[];
  view: Group[];
  schema: Group[];
  table: Group[];
};

const isDatabaseLevel = (objectType: string) => {
  return (
    objectType === "table" || objectType === "view" || objectType === "schema"
  );
};

export const getPrivilegesFromRole = (role: Role | OrgRole) => {
  const privileges: Privileges = {
    engine: [{ toAssign: [], toDeny: [], objects: [] }],
    database: [{ toAssign: [], toDeny: [], objects: [] }],
    account: [{ toAssign: [], toDeny: [], objects: [] }],
    view: [{ toAssign: [], toDeny: [], objects: [] }],
    schema: [{ toAssign: [], toDeny: [], objects: [] }],
    table: [{ toAssign: [], toDeny: [], objects: [] }],
    organization: [{ toAssign: [], toDeny: [], objects: [] }],
    login: [{ toAssign: [], toDeny: [], objects: [] }],
    "service account": [{ toAssign: [], toDeny: [], objects: [] }],
    "network policy": [{ toAssign: [], toDeny: [], objects: [] }],
    role: [{ toAssign: [], toDeny: [], objects: [] }],
  };

  // Pre-group privileges by object type
  const privilegesByType = new Map<string, any[]>();

  for (const privilege of role.privileges) {
    const { objectType } = privilege;
    if (objectType in privileges) {
      if (!privilegesByType.has(objectType)) {
        privilegesByType.set(objectType, []);
      }
      privilegesByType.get(objectType)?.push(privilege);
    }
  }

  // Process each object type separately
  for (const [objectType, typePrivileges] of privilegesByType) {
    const targetPrivileges = privileges[objectType as keyof typeof privileges];
    const objectIsDatabaseLevel = isDatabaseLevel(objectType);

    // First pass: collect all privilege types for each object
    const objectPrivilegesMap = new Map<string, string[]>();
    const objectDetailsMap = new Map<
      string,
      { name: string; catalog?: string }
    >();

    for (const privilege of typePrivileges) {
      const { objectName, privilegeType, objectCatalog } = privilege;

      const objectKey = objectIsDatabaseLevel
        ? `${objectCatalog}.${objectName}`
        : objectName;

      if (!objectPrivilegesMap.has(objectKey)) {
        objectPrivilegesMap.set(objectKey, []);
        objectDetailsMap.set(objectKey, {
          name: objectName,
          catalog: objectCatalog,
        });
      }

      const privileges = objectPrivilegesMap.get(objectKey)!;
      if (!privileges.includes(privilegeType)) {
        privileges.push(privilegeType);
      }
    }

    // Second pass: group objects by their privilege sets
    const privilegeSetToGroup = new Map<string, number>();

    for (const [objectKey, privilegeTypes] of objectPrivilegesMap) {
      // Create a key by sorting for matching, but keep original order for display
      const privilegeSetKey = privilegeTypes.slice().sort().join(",");

      let groupIndex = privilegeSetToGroup.get(privilegeSetKey);

      if (groupIndex === undefined) {
        // Check if we can use the first empty group
        const firstGroup = targetPrivileges[0];
        if (
          firstGroup.objects.length === 0 &&
          firstGroup.toAssign.length === 0
        ) {
          groupIndex = 0;
        } else {
          groupIndex = targetPrivileges.length;
          targetPrivileges.push({
            toAssign: privilegeTypes,
            toDeny: [],
            objects: [],
          });
        }

        privilegeSetToGroup.set(privilegeSetKey, groupIndex);

        // Set the privilege types for this group
        const group = targetPrivileges[groupIndex];
        group.toAssign = privilegeTypes;
      }

      // Add the object to the group
      const group = targetPrivileges[groupIndex];
      const objectDetails = objectDetailsMap.get(objectKey)!;

      if (objectIsDatabaseLevel) {
        group.objects.push({
          catalogName: objectDetails.catalog!,
          objectName: objectDetails.name,
          name: objectDetails.name,
        });
      } else {
        group.objects.push(objectDetails.name);
      }
    }
  }

  return privileges;
};

export const formatPrivilege = (value: string) => {
  return _startCase(value.toLowerCase());
};
